import { useEffect, useState } from "react";
import { useOnlineStatus, useConnectivity } from "../../hooks/useOnlineStatus";
import { cn } from "../../utils/bem";
import { useTranslation } from "../../i18n/LocaleProvider";
import { drainOutbox, pendingCount } from "../../utils/offline-fetch";
import { deadCount, listDead, removeDead, requeueDead, type DeadEntry } from "../../utils/offline-outbox";
import "./OfflinePill.prefix.css";

/**
 * Persistent menubar pill that surfaces two pieces of state:
 *
 *   1. Online vs offline. ``useOnlineStatus`` says when the cloud
 *      Django is unreachable. We render a red wifi-off pill (the
 *      original behaviour) so the annotator knows their writes are
 *      queueing locally.
 *   2. Pending offline writes. ``pendingCount()`` returns the
 *      outbox size. When > 0 we render a SECOND pill — a circular
 *      cloud-with-up-arrow icon with the count as a superscript
 *      badge — so the user can see at a glance how many annotations
 *      are queued for replay. Stays visible online too while the
 *      outbox drains, so the user doesn't navigate away thinking
 *      everything synced.
 *
 * The cloud-up pill is intentionally a separate node (not folded
 * into the offline pill) because the two states are independent: a
 * fresh online user with zero queued items should see nothing; an
 * offline user with zero queued items sees only the wifi-off pill;
 * a user with queued items mid-replay sees only the cloud pill;
 * during an offline session with submits both pills appear together.
 */

const IconWifiOff = (props: React.SVGProps<SVGSVGElement>) => (
  <svg
    viewBox="0 0 24 24"
    width="14"
    height="14"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden
    {...props}
  >
    <line x1="1" y1="1" x2="23" y2="23" />
    <path d="M16.72 11.06A10.94 10.94 0 0 1 19 12.55" />
    <path d="M5 12.55a10.94 10.94 0 0 1 5.17-2.39" />
    <path d="M10.71 5.05A16 16 0 0 1 22.58 9" />
    <path d="M1.42 9a15.91 15.91 0 0 1 4.7-2.88" />
    <path d="M8.53 16.11a6 6 0 0 1 6.95 0" />
    <line x1="12" y1="20" x2="12.01" y2="20" />
  </svg>
);

/** Cloud with a slash — connected, but the server isn't answering. */
const IconCloudOff = (props: React.SVGProps<SVGSVGElement>) => (
  <svg
    viewBox="0 0 24 24"
    width="14"
    height="14"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden
    {...props}
  >
    <path d="M17.5 19a4.5 4.5 0 1 0-1.07-8.87A6 6 0 0 0 4.13 11.5 4.5 4.5 0 0 0 6.5 19h11Z" />
    <line x1="3" y1="3" x2="21" y2="21" />
  </svg>
);

/** Gauge — connected but slow. */
const IconSlow = (props: React.SVGProps<SVGSVGElement>) => (
  <svg
    viewBox="0 0 24 24"
    width="14"
    height="14"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden
    {...props}
  >
    <path d="M12 20a8 8 0 1 1 8-8" />
    <path d="M12 12l4-3" />
    <circle cx="12" cy="12" r="1" />
  </svg>
);

/** Cloud with an upward arrow inside — universal "queued for upload"
 *  glyph. Uses currentColor so the surrounding pill controls the hue
 *  (online = neutral/blue tone, offline = red to match the wifi-off
 *  pill). The arrow lives in its own <g> with class
 *  ``queued-pill__arrow`` so the prefix.css can animate it
 *  up-and-fade to read as "uploading right now". 14×14 to match
 *  the wifi-off icon. */
const IconCloudUp = (props: React.SVGProps<SVGSVGElement>) => (
  <svg
    viewBox="0 0 24 24"
    width="14"
    height="14"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden
    {...props}
  >
    {/* Cloud silhouette */}
    <path d="M17.5 19a4.5 4.5 0 1 0-1.07-8.87A6 6 0 0 0 4.13 11.5 4.5 4.5 0 0 0 6.5 19h11Z" />
    {/* Up arrow inside — animated via CSS in OfflinePill.prefix.css */}
    <g className="queued-pill__arrow">
      <line x1="12" y1="14" x2="12" y2="9" />
      <polyline points="9 12 12 9 15 12" />
    </g>
  </svg>
);

/** Warning triangle for the failed-writes pill. */
const IconWarn = (props: React.SVGProps<SVGSVGElement>) => (
  <svg
    viewBox="0 0 24 24"
    width="14"
    height="14"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden
    {...props}
  >
    <path d="M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" />
    <line x1="12" y1="9" x2="12" y2="13" />
    <line x1="12" y1="17" x2="12.01" y2="17" />
  </svg>
);

/** Human-readable label for a dead-lettered request. */
const describeDead = (d: DeadEntry): string => {
  let path = d.url;
  try {
    if (path.startsWith("http")) path = new URL(path).pathname;
  } catch (_) {
    /* raw */
  }
  const vote = path.match(/\/api\/annotations\/(-?\d+)\/validate/);
  if (vote) return `Listen vote (annotation ${vote[1]})`;
  const create = path.match(/\/api\/tasks\/(\d+)\/(annotations|drafts)/);
  if (create) return create[2] === "drafts" ? `Draft (task ${create[1]})` : `Annotation (task ${create[1]})`;
  const edit = path.match(/\/api\/(annotations|drafts)\/(-?\d+)/);
  if (edit) {
    const noun = edit[1] === "drafts" ? "Draft" : "Annotation";
    const verb = d.method === "DELETE" ? "delete" : "edit";
    return `${noun} ${verb} (${edit[2]})`;
  }
  return `${d.method} ${path}`;
};

export const OfflinePill = () => {
  const { t } = useTranslation();
  const online = useOnlineStatus();
  const connectivity = useConnectivity();
  const [pending, setPending] = useState(0);
  const [dead, setDead] = useState(0);
  const [reviewOpen, setReviewOpen] = useState(false);
  const [deadEntries, setDeadEntries] = useState<DeadEntry[]>([]);

  // Poll the outbox. 3s is the tradeoff: fresh enough for the
  // counter to react to a Submit in user-perceptible time, slow
  // enough that we're not hammering IDB for nothing.
  useEffect(() => {
    let cancelled = false;
    const tick = async () => {
      try {
        const n = await pendingCount();
        if (!cancelled) setPending(n);
      } catch {
        if (!cancelled) setPending(0);
      }
      try {
        const d = await deadCount();
        if (!cancelled) setDead(d);
      } catch {
        if (!cancelled) setDead(0);
      }
    };
    tick();
    // 10s + skip-when-hidden: this poll reads IndexedDB counters; at
    // 3s with the tab backgrounded it was pure battery burn. The
    // ``afri:outbox-dead`` listener below still gives instant
    // feedback on real events.
    const id = window.setInterval(() => {
      if (!document.hidden) tick();
    }, 10000);
    // Instant refresh the moment a replay is rejected — the 3s poll
    // alone would let the queue pill hit zero with no trace for a
    // beat, which reads as "synced fine".
    const onDead = () => void tick();
    window.addEventListener("afri:outbox-dead", onDead);
    return () => {
      cancelled = true;
      window.clearInterval(id);
      window.removeEventListener("afri:outbox-dead", onDead);
    };
  }, []);

  const openReview = async () => {
    try {
      setDeadEntries(await listDead());
    } catch {
      setDeadEntries([]);
    }
    setReviewOpen(true);
  };
  const refreshReview = async () => {
    try {
      const rows = await listDead();
      setDeadEntries(rows);
      setDead(rows.length);
      if (rows.length === 0) setReviewOpen(false);
    } catch {
      /* keep current view */
    }
  };
  const retryOne = async (id?: number) => {
    if (id == null) return;
    await requeueDead(id);
    void drainOutbox();
    await refreshReview();
  };
  const discardOne = async (id?: number) => {
    if (id == null) return;
    await removeDead(id);
    await refreshReview();
  };

  const offlineBlock = cn("offline-pill");
  const queuedBlock = cn("queued-pill");
  const queuedLabel = pending === 1 ? "1 annotation queued for sync" : `${pending} annotations queued for sync`;

  return (
    <>
      {/* Three distinct degraded states, not one "Offline":
            no-internet  — the radio is off / no route (red, wifi-off)
            server-down  — you have internet but our cloud isn't
                           answering (red, cloud-slash) — not your fault
            slow         — cloud answers but slowly (amber, gauge) —
                           nothing broken, expect delays; queued work
                           still syncs
          Each carries a title with the exact meaning + what to expect. */}
      {!online || connectivity === "slow" ? (
        <span
          className={offlineBlock
            .mod({ kind: !online ? (connectivity === "no-internet" ? "no-internet" : "server-down") : "slow" })
            .toClassName()}
          role="status"
          aria-live="polite"
          title={
            !online
              ? connectivity === "no-internet"
                ? "No internet connection. Your work is saved on this device and will sync when you're back online."
                : "You're connected, but the AfriAnnotate server isn't reachable right now. Your work is saved on this device and will sync when it recovers."
              : "Slow connection. Everything still works, saves may take a moment to sync."
          }
        >
          {!online ? connectivity === "no-internet" ? <IconWifiOff /> : <IconCloudOff /> : <IconSlow />}
          {!online && pending > 0 && (
            <span className={offlineBlock.elem("badge").toClassName()} aria-label={`${pending} queued`}>
              {pending > 99 ? "99+" : pending}
            </span>
          )}
          <span className={offlineBlock.elem("label").toClassName()}>
            {!online
              ? connectivity === "no-internet"
                ? t("components.offlinepill.offline_pill.offline", undefined, "Offline")
                : "Server unreachable"
              : "Slow"}
          </span>
        </span>
      ) : null}
      {/* Offline AND queued: the queue count rides ON the offline pill
          as a badge — two side-by-side red pills read as clutter on a
          phone menubar (owner feedback). The separate cloud-up pill
          only renders while online. */}
      {pending > 0 && online ? (
        <span
          className={queuedBlock.mod({ online }).toClassName()}
          role="status"
          aria-live="polite"
          aria-label={queuedLabel}
          title={queuedLabel}
        >
          <IconCloudUp />
          {/* Superscript-style badge that reads count. Capped at 99+
              so the pill geometry doesn't blow out. */}
          <span className={queuedBlock.elem("badge").toClassName()}>{pending > 99 ? "99+" : pending}</span>
        </span>
      ) : null}
      {dead > 0 ? (
        <button
          type="button"
          className={cn("failed-pill").toClassName()}
          aria-label={`${dead} failed sync${dead === 1 ? "" : "s"}, tap to review`}
          title={`${dead} write${dead === 1 ? "" : "s"} the server rejected, tap to review`}
          onClick={() => void openReview()}
        >
          <IconWarn />
          <span className={cn("failed-pill").elem("badge").toClassName()}>{dead > 99 ? "99+" : dead}</span>
        </button>
      ) : null}
      {reviewOpen ? (
        <div
          className={cn("failed-review").toClassName()}
          role="dialog"
          aria-modal="true"
          aria-label="Failed syncs"
          onClick={(e) => {
            if (e.target === e.currentTarget) setReviewOpen(false);
          }}
        >
          <div className={cn("failed-review").elem("card").toClassName()}>
            <div className={cn("failed-review").elem("head").toClassName()}>
              <strong>Failed syncs</strong>
              <button type="button" onClick={() => setReviewOpen(false)} aria-label="Close">
                ✕
              </button>
            </div>
            <p className={cn("failed-review").elem("lede").toClassName()}>
              The server rejected these queued writes. Retry after fixing the cause (e.g. the project was re-enabled for
              mobile), or discard.
            </p>
            <ul className={cn("failed-review").elem("list").toClassName()}>
              {deadEntries.map((d) => (
                <li key={d.id} className={cn("failed-review").elem("row").toClassName()}>
                  <div className={cn("failed-review").elem("what").toClassName()}>
                    <span>{describeDead(d)}</span>
                    <small>
                      HTTP {d.failStatus}, queued {new Date(d.queuedAt).toLocaleString()}
                      {d.failDetail ? `, ${d.failDetail.slice(0, 120)}` : ""}
                    </small>
                  </div>
                  <div className={cn("failed-review").elem("actions").toClassName()}>
                    <button type="button" onClick={() => void retryOne(d.id)}>
                      Retry
                    </button>
                    <button type="button" onClick={() => void discardOne(d.id)}>
                      Discard
                    </button>
                  </div>
                </li>
              ))}
            </ul>
          </div>
        </div>
      ) : null}
    </>
  );
};
